import { useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { CircleCheck, CircleX } from 'lucide-react';
import { autoTestLogApi } from '@/api/production/logs';
import type { IAutoTestLog } from '@/types/production/auto-test-log';
import { DEVICE_MODEL_LABELS } from '@/constants/device';

function getStatusProps(value: boolean) {
  const statusClass = value ? 'text-green-500' : 'text-red-500';
  const Icon = value ? CircleCheck : CircleX;
  const text = value ? 'Sucesso' : 'Falha';
  return { Icon, statusClass, text };
}

export function AutoTestLogDetailPage() {
  const { id } = useParams<{ id: string }>();

  const { data: log, isLoading, error } = useQuery({
    queryKey: ['auto-test-log', id],
    queryFn: () => autoTestLogApi.findOne(id!),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="flex flex-wrap items-center gap-6 px-4 sm:flex-nowrap sm:px-6 lg:px-8">
        <div className="w-full text-center py-8">
          <p className="text-gray-500">Carregando...</p>
        </div>
      </div>
    );
  }

  if (error || !log) {
    return (
      <div className="flex flex-wrap items-center gap-6 px-4 sm:flex-nowrap sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          <h3 className="text-base/7 font-semibold text-gray-900">
            Auto Test Log Information Not Found
          </h3>
          <p className="mt-1 max-w-2xl text-sm/6 text-gray-500">
            Please look for other information.
          </p>
        </div>
      </div>
    );
  }

  return <AutoTestLogDescription data={log} />;
}

interface AutoTestLogDescriptionProps {
  data: IAutoTestLog;
}

function AutoTestLogDescription({ data }: AutoTestLogDescriptionProps) {
  const formattedCreatedAt = new Date(data.created_at).toLocaleString();
  const { Icon: StatusIcon, statusClass, text } = getStatusProps(data.status);

  return (
    <div className="flex flex-wrap items-center gap-6 px-4 sm:flex-nowrap sm:px-6 lg:px-8">
      <div className="w-full">
        <div className="px-4 sm:px-0">
          <h3 className="text-base/7 font-semibold text-gray-900">
            Auto Test Log Information
          </h3>
          <p className="mt-1 max-w-2xl text-sm/6 text-gray-500">
            Detailed information of the automatic test log.
          </p>
        </div>
        <div className="mt-6 border-t border-gray-200">
          <dl className="divide-y divide-gray-200">
            {/* Test Result */}
            <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
              <dt className="text-sm/6 font-medium text-gray-900">Result</dt>
              <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0 flex items-center">
                <StatusIcon className={`h-5 w-5 ${statusClass}`} />
                <span className={`ml-2 ${statusClass}`}>{text}</span>
              </dd>
            </div>

            {/* Analysis */}
            {data.analysis && Object.keys(data.analysis).length > 0 && (
              <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                <dt className="text-sm/6 font-medium text-gray-900">Analysis</dt>
                <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                  <ul className="space-y-2">
                    {Object.entries(data.analysis).map(([key, value]) => {
                      const { Icon, statusClass: itemStatusClass } = getStatusProps(
                        value as boolean
                      );
                      return (
                        <li key={key} className="flex items-center">
                          <Icon className={`h-5 w-5 ${itemStatusClass}`} />
                          <span className={`ml-2 ${itemStatusClass}`}>{key}</span>
                        </li>
                      );
                    })}
                  </ul>
                </dd>
              </div>
            )}

            {/* Equipment Information */}
            {data.equipment.imei && (
              <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                <dt className="text-sm/6 font-medium text-gray-900">IMEI</dt>
                <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                  {data.equipment.imei}
                </dd>
              </div>
            )}

            {data.equipment.serial && (
              <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                <dt className="text-sm/6 font-medium text-gray-900">Serial</dt>
                <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                  {data.equipment.serial}
                </dd>
              </div>
            )}

            <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
              <dt className="text-sm/6 font-medium text-gray-900">Firmware</dt>
              <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                {data.equipment.firmware}
              </dd>
            </div>

            {data.equipment.lora_version && (
              <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                <dt className="text-sm/6 font-medium text-gray-900">Firmware LoRa</dt>
                <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                  {data.equipment.lora_version}
                </dd>
              </div>
            )}

            {data.equipment.ble_version && (
              <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                <dt className="text-sm/6 font-medium text-gray-900">Firmware BLE</dt>
                <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                  {data.equipment.ble_version}
                </dd>
              </div>
            )}

            {data.equipment.iccid && (
              <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                <dt className="text-sm/6 font-medium text-gray-900">ICCID</dt>
                <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                  {data.equipment.iccid}
                </dd>
              </div>
            )}

            {/* Technology */}
            <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
              <dt className="text-sm/6 font-medium text-gray-900">Technology</dt>
              <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                {DEVICE_MODEL_LABELS[data.technology.system_name as keyof typeof DEVICE_MODEL_LABELS] || data.technology.system_name}
              </dd>
            </div>

            {/* Created At */}
            <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
              <dt className="text-sm/6 font-medium text-gray-900">Created At</dt>
              <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                {formattedCreatedAt}
              </dd>
            </div>

            {/* User ID */}
            <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
              <dt className="text-sm/6 font-medium text-gray-900">User ID</dt>
              <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                {data.user.name}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
}
